import { ConversationsMessage, CreateConversationsMessage, privateApi } from 'services';
import { Conversation, CreateConversationEmail, CreateConversationSms } from './types';
import { Iri } from 'lib/utils/iri';
import { TalentConversationLocation } from 'lib/enum/lead-conversation-location';
import { privateApiWithRouteLeavingCancelation } from '../ApiService/ApiServiceWithRouteLeavingCancelation';

const createConversationMessage = (
    message: CreateConversationsMessage | CreateConversationSms | CreateConversationEmail,
    conversationId: string,
): Promise<ConversationsMessage> =>
    privateApi.post<ConversationsMessage>(`leads-conversations/${conversationId}/messages`, message);

const markConversationMessagesAsRead = (talentId: string, messageIris: Iri[]): Promise<void> =>
    privateApi.post<void>(`leads-conversations/${talentId}/bulk-mark-messages-as-read`, {
        messages: messageIris,
    });

const markConversationAsRead = (conversationId: string): Promise<Conversation> =>
    privateApi.post<Conversation>(`leads-conversations/${conversationId}/mark-as-read`);

const markConversationAsUnread = (conversationId: string): Promise<Conversation> =>
    privateApi.post<Conversation>(`leads-conversations/${conversationId}/mark-as-unread`);

const changeConversationLocation = (talentId: string, location: TalentConversationLocation): Promise<void> =>
    privateApi.put(`leads-conversations/${talentId}/location`, {
        location,
    });

const downloadConversationPdf = async (conversationId: string): Promise<Blob> => {
    const response = await privateApi.get<Response>(`leads/conversation-pdf/${conversationId}`, {
        returnRawResponse: true,
    });
    return response.blob();
};

export const ConversationGateway = {
    createConversationMessage,
    markConversationMessagesAsRead,
    markConversationAsRead,
    markConversationAsUnread,
    changeConversationLocation,
    downloadConversationPdf,
};

const cancelableFetchConversation = async (talentId: string, settings?: Record<string, any>): Promise<Conversation> =>
    privateApiWithRouteLeavingCancelation.get<Conversation>(`leads-conversations/${talentId}`, settings);

const cancelableFetchConversationMessage = (messageId: string): Promise<ConversationsMessage> =>
    privateApiWithRouteLeavingCancelation.get<ConversationsMessage>(`talent-messages/${messageId}`);

const cancelableCreateConversationMessage = (
    message: CreateConversationsMessage | CreateConversationSms | CreateConversationEmail,
    conversationId: string,
    eventName: string,
): Promise<ConversationsMessage> =>
    privateApiWithRouteLeavingCancelation.postWithAbortControlOnCustomEvent<ConversationsMessage>(
        `leads-conversations/${conversationId}/messages`,
        message,
        { customEventName: eventName },
    );

export const CancelableConversationGateway = {
    cancelableFetchConversation,
    cancelableFetchConversationMessage,
    cancelableCreateConversationMessage,
};
